/**
 * Client for the PUBLIC universal self-check-in edge function (self-checkin, verify_jwt=false).
 * Used by the always-on /self-checkin page. Raw fetch with the anon apikey (visitor is unauthenticated).
 */

const FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/self-checkin`;
const ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

async function call(
  payload: Record<string, unknown>,
): Promise<{ body: Record<string, unknown>; status: number }> {
  const res = await fetch(FN_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: ANON,
      Authorization: `Bearer ${ANON}`,
    },
    body: JSON.stringify(payload),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  return { body, status: res.status };
}

// Public-safe shape returned by list_self_checkin_events_v3 — includes the public event title but
// no street / postal / contact data (this endpoint is anonymous). `all_day`/`date_tbd` drive whether
// the start time is shown.
export interface SelfCheckinEvent {
  event_id: string;
  start_time: string | null;
  all_day: boolean;
  date_tbd: boolean;
  timezone: string | null;
  location_name: string | null;
  location_city: string | null;
  location_state: string | null;
  /** Event name — shown as the primary "Checking in at" label (#3); returned directly by v3. */
  title: string | null;
}

/** Events happening today, each in its own timezone (server-derived). No client date is sent.
 * `disabled` = the global kill-switch is off. */
export async function listSelfCheckinEvents(): Promise<{
  events: SelfCheckinEvent[];
  disabled: boolean;
}> {
  const { body } = await call({ action: "list_events" });
  return {
    events: (body.events as SelfCheckinEvent[]) ?? [],
    disabled: !!body.disabled,
  };
}

export type ScanKind = "admission" | "voucher" | "token";
export interface ScanResolution {
  /** HTTP status of the resolve call — 429/5xx MUST NOT be read as "code not found"
   *  (BAM! 2026-07-07: rate-limit rejections rendered as "code wasn't recognized"). */
  status: number;
  found: boolean;
  kind?: ScanKind;
  eventId?: string | null;
  eventTitle?: string | null;
  alreadyCheckedIn?: boolean;
  disabled?: boolean;
}

/** Classify a scanned/typed code. */
export async function resolveScan(code: string): Promise<ScanResolution> {
  const { body, status } = await call({ action: "resolve_scan", code });
  return {
    status,
    found: !!body.found,
    kind: body.kind as ScanKind | undefined,
    eventId: (body.eventId as string | null) ?? null,
    eventTitle: (body.eventTitle as string | null) ?? null,
    alreadyCheckedIn: !!body.alreadyCheckedIn,
    disabled: !!body.disabled,
  };
}

export interface ScanCheckinResult {
  status: number;
  checkedIn?: boolean;
  alreadyCheckedIn?: boolean;
  eventTitle?: string | null;
  attendeeName?: string | null;
  message?: string;
  /** Back-compat: now sourced from CONFIRMED provider acceptance (equals emailAccepted). */
  voucherEmailed?: boolean;
  /** The reward email was CONFIRMED accepted by the provider. Only then say "sent". */
  emailAccepted?: boolean;
  /** The send threw after dispatch — being reconciled, never resent. */
  emailAmbiguous?: boolean;
  /** No on-file email — nothing was sent; the code/QR is the deliverable. */
  emailSkippedNoEmail?: boolean;
  /** Honest ticket disposition: the check-in succeeded but the ticket/reward did NOT (retryable). */
  ticketFailed?: boolean;
  /** The ticket/reward issued, but its branded email was not confirmed sent this call. */
  ticketPending?: boolean;
  maskedEmail?: string | null;
  kind?: string;
  /** For a scanned ticket (TK-) admitted at the tablet: the admission code + its QR, shown on-screen so
   *  the holder can show it at entry. */
  rewardTicketCode?: string | null;
  rewardTicketQrUrl?: string | null;
  rewardState?: string | null;
  /** Name of the reward to redeem later (the event ticket's escalation award), if configured. */
  reward_offer_name?: string | null;
  error?: string;
}

/** Check in a scanned admission ticket (direct) or voucher (member identity). */
export async function checkinScan(
  code: string,
  attemptId?: string,
): Promise<ScanCheckinResult> {
  // attempt_id: anonymous per-visit id (see newAttemptId) so a failed member lookup can be linked to a later
  // check-in in the lookup log; it identifies nobody.
  const { body, status } = await call({
    action: "checkin_scan",
    code,
    attempt_id: attemptId,
  });
  return { status, ...(body as object) };
}

/**
 * Another person on the confirmed member's OWN membership account (a "secondary"), offered after
 * they check in so they can add them as their guest. The email is MASKED by the server -- the
 * kiosk never receives a co-member's real address; the ticket email is sent server-side.
 */
export interface SelfCheckinSecondary {
  contact_id: string;
  first_name: string | null;
  last_name: string | null;
  masked_email: string | null;
  /** False => no address on file, so no ticket can be delivered; the option is shown disabled. */
  has_email: boolean;
  already_checked_in: boolean;
}

export interface WalkinResult {
  status: number;
  checkedIn?: boolean;
  isMember?: boolean;
  ticketCode?: string | null;
  /** The TK- Event Ticket code carrying the reward, to redeem at /redeem (Phase D). */
  rewardTicketCode?: string | null;
  /** The admission QR for the minted walk-in ticket — shown on-screen to photograph. */
  rewardTicketQrUrl?: string | null;
  /** 'pending' = reward value assigned later; 'assigned' = redeemable now. */
  rewardState?: string | null;
  voucherEmailed?: boolean;
  /** #7: the reward QR + /redeem link was emailed to the holder on this no-QR walk-in. */
  rewardEmailed?: boolean;
  /** Audit MED#4: emailed, but the QR image could not be produced — only the text code was sent. */
  rewardEmailedWithoutQr?: boolean;
  maskedEmail?: string | null;
  /** Name of the reward to redeem later (the event ticket's escalation award), if configured. */
  reward_offer_name?: string | null;
  /** mode='member' only: others on this member's membership account, to offer as guests. */
  secondaries?: SelfCheckinSecondary[];
  /**
   * Short-lived signed capability proving this member just confirmed their identity. Each add
   * presents it INSTEAD of re-sending identity fields, so the add path never runs the identity
   * resolver and cannot be used to guess a ZIP (external audit round 2, blocker 1).
   */
  secondaryToken?: string | null;
  /** epoch seconds; the kiosk stops offering adds once it passes. */
  secondaryTokenExpiresAt?: number | null;
  error?: string;
}

/** The success-screen view for a completed walk-in (title is set by the caller). */
export interface WalkinSuccessView {
  detail?: string;
  rewardCode: string | null;
  rewardQrUrl: string | null;
  rewardName: string | null;
}

/**
 * Build the walk-in success screen from a WalkinResult.
 *
 * The minted TK- ticket (rewardTicketCode) IS the admission ticket + reward code, so its code + QR are shown
 * whenever the ticket exists — INDEPENDENT of whether the OPTIONAL reward NAME resolved (the backend returns
 * reward_offer_name=null on a transient lookup failure). The reward name only enriches the "claim your <name>"
 * line; a name-lookup failure must NEVER hide the ticket (audit MED#1). Pure/synchronous → unit-testable.
 */
export function buildWalkinSuccessView(res: WalkinResult): WalkinSuccessView {
  const ticketCode = res.rewardTicketCode ?? null;
  const rewardName = ticketCode ? (res.reward_offer_name ?? null) : null;
  const parts: string[] = [];
  // 2026-09-08 owner rule: check-in mints a TICKET; only the operator voucher upgrade makes it a
  // voucher. Nothing here names or values a reward, so the member is not promised one.
  if (ticketCode && res.rewardEmailed) {
    const where = res.maskedEmail ? `to ${res.maskedEmail}` : "to you";
    parts.push(
      // Audit MED#4: if the QR image couldn't be attached, say only the text code was emailed.
      res.rewardEmailedWithoutQr
        ? `We also emailed the code ${where} (the QR image couldn't be attached — use the code above).`
        : `We also emailed it ${where}.`,
    );
  }
  return {
    detail: parts.join(" ") || undefined,
    rewardCode: ticketCode,
    rewardQrUrl: ticketCode ? (res.rewardTicketQrUrl ?? null) : null,
    rewardName,
  };
}

export async function walkinMember(input: {
  eventId: string;
  email?: string;
  memberNumber?: string;
  firstName: string;
  lastName: string;
  zip: string;
  attemptId?: string;
}): Promise<WalkinResult> {
  const { body, status } = await call({
    action: "walkin",
    mode: "member",
    attempt_id: input.attemptId,
    event_id: input.eventId,
    email: input.email,
    member_number: input.memberNumber,
    first_name: input.firstName,
    last_name: input.lastName,
    zip: input.zip,
  });
  return { status, ...(body as object) };
}

export async function walkinGuest(input: {
  eventId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  zip?: string;
  // A walk-in guest is not identified as a member and hasn't seen the registration-page disclaimers,
  // so the backend requires explicit consent (#1d). Members and QR/ticket-link holders are not prompted.
  disclaimerAck?: boolean;
  // Optional "here with a member?" — the inviting member's email or member # (companion capture, seam 1).
  invitingMemberEmail?: string;
  invitingMemberNumber?: string;
  // Only for the person's OWN walk-in (not a guest a confirmed member is registering).
  attemptId?: string;
}): Promise<WalkinResult> {
  const { body, status } = await call({
    action: "walkin",
    mode: "guest",
    attempt_id: input.attemptId,
    event_id: input.eventId,
    first_name: input.firstName,
    last_name: input.lastName,
    email: input.email,
    phone: input.phone,
    zip: input.zip,
    disclaimer_ack: input.disclaimerAck === true,
    inviting_member_email: input.invitingMemberEmail,
    inviting_member_number: input.invitingMemberNumber,
  });
  return { status, ...(body as object) };
}

/**
 * Add a person on the confirmed member's own membership account as their guest: the co-member gets
 * their OWN ticket and is checked in directly, and the Operations tab shows "brought by <member>".
 *
 * Presents the `secondaryToken` from the member's own check-in response rather than re-sending
 * identity fields. The server reads the primary's contact id out of the SIGNED payload, so the
 * client cannot change whose membership it enumerates, and the add path runs no identity
 * resolution at all -- which is what closes the guess-ordering bypass the round-2 audit found.
 * `secondaryContactId` comes from WalkinResult.secondaries; the server independently re-derives
 * that list from the signed primary and refuses anyone not on their membership.
 *
 * A 401 with `capabilityInvalid` means the capability expired (or the kiosk was reloaded) and the
 * member simply needs to check themselves in again.
 */
export async function walkinSecondary(input: {
  eventId: string;
  secondaryToken: string;
  secondaryContactId: string;
}): Promise<WalkinResult> {
  const { body, status } = await call({
    action: "walkin",
    mode: "secondary",
    event_id: input.eventId,
    secondary_token: input.secondaryToken,
    secondary_contact_id: input.secondaryContactId,
  });
  return { status, ...(body as object) };
}

/** decodeURIComponent that never throws on a malformed %-sequence (a garbage QR scan) — returns the
 *  raw segment instead of exploding the caller. */
function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/** Extract a usable code from raw scanned text: a bare code, a `?code=` voucher link, a `/t/:code` token
 *  link, or the trailing segment of any other URL. Covers all booth/self-checkin QR shapes. Never throws. */
export function extractScanCode(raw: string): string {
  const t = (raw || "").trim();
  if (!t) {
    return "";
  }
  // A `?code=` query param (voucher redeem links, e.g. /redeem?code=ABC123).
  try {
    const q = new URL(t).searchParams.get("code");
    if (q) {
      return q.trim();
    }
  } catch {
    /* not a URL */
  }
  // A /t/:code multi-behavior token link (Event Ticket admission QR).
  const tMatch = t.match(/\/t\/([^/?#\s]+)/);
  if (tMatch) {
    return safeDecode(tMatch[1]);
  }
  // Any other URL → last non-empty path segment.
  if (/^https?:\/\//i.test(t)) {
    try {
      const u = new URL(t);
      const segs = u.pathname.split("/").filter(Boolean);
      if (segs.length) {
        return safeDecode(segs[segs.length - 1]);
      }
    } catch {
      /* fall through */
    }
  }
  return t;
}

// ---- Email one-click ticket link (self-check-in straight from the ticket email) ----
// The ticket is subject-bound, so the backend reads membership directly: a member is
// checked in automatically; a non-member must confirm disclaimers first.

export interface TicketLinkResolution {
  status: number;
  found?: boolean;
  eventTitle?: string | null;
  firstName?: string | null;
  isMember?: boolean;
  alreadyCheckedIn?: boolean;
  needsDisclaimer?: boolean;
  checkinOpen?: boolean;
  rewardName?: string | null;
  // 'assigned' = a real reward is claimable now; 'pending' = placeholder not yet finalized ($0
  // until assigned) — the page must NOT tell the holder to "claim" it.
  rewardState?: string | null;
  error?: string;
}

/** Resolve a ticket code from the email link → what the page should render. */
export async function ticketLinkResolve(
  code: string,
): Promise<TicketLinkResolution> {
  const { body, status } = await call({ action: "ticket_link_resolve", code });
  return {
    status,
    ...(body as Record<string, unknown>),
  } as TicketLinkResolution;
}

export interface TicketLinkCheckinResult {
  status: number;
  checkedIn?: boolean;
  alreadyCheckedIn?: boolean;
  isMember?: boolean;
  eventTitle?: string | null;
  firstName?: string | null;
  rewardState?: string | null;
  rewardName?: string | null;
  needsDisclaimer?: boolean;
  error?: string;
}

/** Complete the check-in. A non-member MUST pass disclaimerAck=true. */
export async function ticketLinkCheckin(
  code: string,
  disclaimerAck: boolean,
): Promise<TicketLinkCheckinResult> {
  const { body, status } = await call({
    action: "ticket_link_checkin",
    code,
    disclaimer_ack: disclaimerAck,
  });
  return {
    status,
    ...(body as Record<string, unknown>),
  } as TicketLinkCheckinResult;
}
